'use client'

import posthog from 'posthog-js'
import type { CaptureResult } from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import {
  performanceAnalytics,
  isIgnorableClientError,
  captureFirstTouchSnapshot,
  resolveInternalTrafficFlag,
} from '@/lib/analytics'
import { recordSessionEntry } from '@/lib/analytics/entry-source'
import { scrubSensitiveLocationParamsFromUrl } from '@/lib/posthog-masking'
import { adsEligible, readPremiumAdFreeHint } from '@/lib/ads/ads-eligibility'

// Session-replay loading is the largest non-framework long task on the homepage
// (~288ms parse+exec for posthog-recorder.js). Defer it behind first user
// interaction so cold-load LCP/TBT don't pay for it. Engaged users still get
// replay; bouncers don't trigger the long task at all. INP-safety: we schedule
// the actual `startSessionRecording()` call inside requestIdleCallback AFTER
// the interaction event handler returns — running it synchronously inside the
// handler would shift the long task from page-load TBT into INP, which is the
// wrong trade.
let recorderListenersRegistered = false
let recorderActivated = false

export const BASE_SESSION_RECORDING_CONFIG = {
  // Every input value is replaced with asterisks in replay (rrweb's built-in
  // masking). No maskInputFn on purpose: once one is set, rrweb uses its return
  // value verbatim for every masked input, so a pass-through function would
  // unmask them. Emails, names, addresses and ZIPs all stay masked.
  maskAllInputs: true,
  // Network payload capture stays off in code whatever the remote project
  // setting says: posthog-js lets a client `false` override the remote value
  // for bodies and headers. They can carry auth tokens and personal data.
  recordBody: false,
  recordHeaders: false,
  maskInputOptions: {
    password: true,
    email: true,
  },
  // Drop inert JSON-LD and framework script nodes from replay snapshots.
  // rrweb serializes script text as "SCRIPT_PLACEHOLDER"; without this,
  // those placeholders can show up in PostHog's DOM snapshot viewer even
  // though visitors never see them in the real page.
  slimDOMOptions: {
    script: true,
  },
  collectFonts: true,
  // Record stylesheets as <link> references and let the PostHog player
  // refetch them at replay time. Inlining cssRules requires CORS access to
  // every stylesheet, and Next's runtime CSS-chunk injector does not
  // reliably propagate the `crossorigin` attribute — the result was blank
  // / unstyled replays in production. Linked stylesheets are rrweb's
  // default.
  inlineStylesheet: false,
}

export const POSTHOG_PERFORMANCE_CONFIG = {
  web_vitals_allowed_metrics: ['LCP', 'INP', 'CLS'] as Array<'LCP' | 'INP' | 'CLS'>,
}

function armRecorder() {
  if (recorderListenersRegistered) return
  recorderListenersRegistered = true

  const activate = () => {
    if (recorderActivated) return
    recorderActivated = true
    document.removeEventListener('pointerdown', activate)
    document.removeEventListener('keydown', activate)
    document.removeEventListener('scroll', activate)

    const start = () => startSessionRecordingForCurrentRoute()
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 1000 })
    } else {
      setTimeout(start, 50)
    }
  }

  const opts = { passive: true }
  document.addEventListener('pointerdown', activate, opts)
  document.addEventListener('keydown', activate, opts)
  document.addEventListener('scroll', activate, opts)
}

function startSessionRecordingForCurrentRoute() {
  if (window.location.pathname.startsWith('/blog')) return
  posthog.set_config({ session_recording: BASE_SESSION_RECORDING_CONFIG })
  const start = () => posthog.startSessionRecording()
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(start, { timeout: 1000 })
  } else {
    setTimeout(start, 50)
  }
}

// Drop browser-extension / scanner / third-party `$exception` noise at the one
// chokepoint every event passes through. The message-level filter in
// lib/analytics.ts only guards our own captureError() and window.onerror paths;
// PostHog's built-in exception autocapture hooks window.onerror/onunhandled-
// rejection independently and never passes through it. before_send is the only
// place that catches exceptions from ALL capture paths.
export function isIgnorableExceptionEvent(event: CaptureResult): boolean {
  if (event.event !== '$exception') return false
  const props = event.properties
  if (!props) return false

  const parts: string[] = []
  const values = props.$exception_values
  if (Array.isArray(values)) parts.push(...values.map((v) => String(v ?? '')))

  const list = props.$exception_list
  if (Array.isArray(list)) {
    for (const item of list) {
      if (!item || typeof item !== 'object') continue
      const entry = item as {
        value?: unknown
        type?: unknown
        stacktrace?: {
          frames?: Array<{
            source?: unknown
            // Raw posthog-js frames carry the script URL in `filename`;
            // `source` only exists after server-side symbolication, which
            // hasn't happened yet at before_send. Same raw-vs-symbolicated
            // trap the styled-jsx/deleteRule note in lib/analytics.ts records.
            filename?: unknown
            resolved_name?: unknown
            mangled_name?: unknown
            function?: unknown
            module?: unknown
          }>
        }
      }
      if (entry.value != null) parts.push(String(entry.value))
      if (entry.type != null) parts.push(String(entry.type))
      const frames = entry.stacktrace?.frames
      if (Array.isArray(frames)) {
        for (const frame of frames) {
          if (frame?.source != null) parts.push(String(frame.source))
          if (frame?.filename != null) parts.push(String(frame.filename))
          if (frame?.resolved_name != null) parts.push(String(frame.resolved_name))
          if (frame?.mangled_name != null) parts.push(String(frame.mangled_name))
          if (frame?.function != null) parts.push(String(frame.function))
          if (frame?.module != null) parts.push(String(frame.module))
        }
      }
    }
  }

  return parts.some((part) => isIgnorableClientError(part))
}

// A CSP block is silent by design: the browser refuses the resource and the
// page renders on as if nothing happened. That is how the Grow.me loader sat
// inert for weeks and how the launch-night journeymv wrapper shipped blocked —
// both were found by accident, after the fact. `securitypolicyviolation` is the
// only proactive signal the browser offers, so forward it to PostHog instead of
// depending on someone reading a console on the right page at the right time.
//
// The listener registers at module eval, well before posthog.init() runs in
// PHProvider's effect, and script-src violations fire during head parsing —
// earlier still. So violations queue and flush once the SDK is live rather than
// being dropped by a capture() on an uninitialized client.
const MAX_CSP_VIOLATIONS_PER_PAGE = 10
const seenCspViolations = new Set<string>()
let pendingCspViolations: Record<string, unknown>[] = []

function flushCspViolations() {
  if (!(posthog as unknown as { __loaded?: boolean }).__loaded) return
  if (pendingCspViolations.length === 0) return
  const queued = pendingCspViolations
  pendingCspViolations = []
  for (const properties of queued) {
    posthog.capture('csp_violation', properties)
  }
}

// blockedURI is a full URL for external resources but a bare keyword ('inline',
// 'eval', 'data') for the others. Group by origin so one blocked vendor is one
// row no matter how many asset paths it tried.
function cspBlockedOrigin(blockedUri: string): string {
  if (!blockedUri.includes('://')) return blockedUri || 'unknown'
  try {
    return new URL(blockedUri).origin
  } catch {
    return 'unparseable'
  }
}

// Per page load (module scope; a hard navigation resets it). posthog-js holds
// the first $pageview until the tab is visible, but still sends $pageleave on
// unload, so a page that was never seen (Safari top-hit preload, background
// tab) produced a session holding only a $pageleave.
let pageviewSentThisLoad = false

// ads_eligible for soft navigations. posthog-js captures a history-change
// $pageview synchronously inside pushState/popstate, before React commits the
// new route, so the DOM still shows the previous page's ad markers. Paths are
// recorded once their DOM has settled and reused on later soft visits; a first
// soft visit to a path carries no ads_eligible. (Journey runs one auction per
// document load anyway, so the hard-load pageview is the one that matters.)
const adsEligibleByPath = new Map<string, boolean>()

// The Journey loader decides from the premium hint once, in <head>, before this
// module evaluates; a hint written later in the load does not change this
// document's ads.
let premiumAdFreeAtLoad = false
if (typeof window !== 'undefined') {
  try {
    premiumAdFreeAtLoad = readPremiumAdFreeHint(window.localStorage)
  } catch {
    premiumAdFreeAtLoad = false
  }
}

export function recordAdsEligibilityForCurrentPath(): void {
  if (typeof document === 'undefined') return
  adsEligibleByPath.set(window.location.pathname, adsEligible(document, premiumAdFreeAtLoad))
}

/** Test-only: reset the per-load state this module keeps. */
export function resetPostHogPageStateForTests(premiumAdFree = false): void {
  pageviewSentThisLoad = false
  adsEligibleByPath.clear()
  premiumAdFreeAtLoad = premiumAdFree
}

export function scrubPostHogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event?.properties) return event

  // Returning null tells posthog-js to discard the event entirely.
  if (isIgnorableExceptionEvent(event)) return null

  if (event.event === '$pageleave' && !pageviewSentThisLoad) return null

  // Every URL-bearing property, not a fixed list: posthog-js also stamps
  // $session_entry_url / $session_entry_referrer on every event of a session,
  // and the person's first-touch $initial_* URLs are stored forever.
  for (const bag of [event.properties, event.properties.$set, event.properties.$set_once, event.$set, event.$set_once]) {
    if (!bag || typeof bag !== 'object') continue
    for (const key of Object.keys(bag)) {
      if (/url|referrer/i.test(key) && typeof bag[key] === 'string') {
        bag[key] = scrubSensitiveLocationParamsFromUrl(bag[key])
      }
    }
  }

  if (event.event === '$pageview') {
    pageviewSentThisLoad = true
    if (typeof document !== 'undefined') {
      const softNavigation = typeof event.properties.navigation_type === 'string'
      if (!softNavigation) recordAdsEligibilityForCurrentPath()
      const eligible = adsEligibleByPath.get(window.location.pathname)
      if (eligible !== undefined) event.properties.ads_eligible = eligible
    }
  }

  return event
}

if (typeof window !== 'undefined') {
  // Global error handlers for PostHog tracking
  // These catch errors that React's ErrorBoundary won't (e.g., event handlers, async code)
  window.addEventListener('error', (event) => {
    // Ignore browser-extension and cross-origin script noise (see the shared
    // signature list in lib/analytics.ts — captureError filters too, this just
    // skips building the payload for the common case).
    if (isIgnorableClientError(event.message, event.filename, event.error?.stack)) {
      return
    }

    performanceAnalytics.captureError({
      error: event.error || new Error(event.message),
      source: 'window',
      severity: 'error',
      context: {
        filename: event.filename?.slice(0, 100),
        line_number: event.lineno,
        column_number: event.colno,
      }
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    // Same noise filter the `error` handler above applies — third-party ad
    // scripts (journeymv/scriptwrapper) reject promises constantly and were
    // flooding Error Tracking (~1,300/day, 2026-07-31) because this handler
    // forwarded every rejection unfiltered.
    const reason: unknown = event.reason
    const reasonMessage =
      reason instanceof Error ? reason.message : String(reason ?? '')
    const reasonStack = reason instanceof Error ? reason.stack : undefined
    if (isIgnorableClientError(reasonMessage, undefined, reasonStack)) {
      return
    }

    performanceAnalytics.captureError({
      error: event.reason,
      source: 'promise',
      severity: 'warning',
      context: {
        promise_type: typeof event.reason,
      }
    })
  })

  document.addEventListener('securitypolicyviolation', (event) => {
    const blockedUri = event.blockedURI || ''
    const sourceFile = event.sourceFile || ''

    // Extensions inject their own scripts and styles into the page and trip our
    // policy constantly — the same noise class the two handlers above filter.
    if (isIgnorableClientError(blockedUri, sourceFile)) return

    // A blocked resource inside a render loop fires this event hundreds of
    // times, so record one row per (directive, blocked origin) per page load.
    const directive = event.effectiveDirective || event.violatedDirective
    const key = `${directive}|${cspBlockedOrigin(blockedUri)}`
    if (seenCspViolations.has(key)) return
    if (seenCspViolations.size >= MAX_CSP_VIOLATIONS_PER_PAGE) return
    seenCspViolations.add(key)

    pendingCspViolations.push({
      directive,
      blocked_origin: cspBlockedOrigin(blockedUri),
      blocked_uri: blockedUri.slice(0, 200),
      source_file: sourceFile.slice(0, 200) || undefined,
      line_number: event.lineNumber || undefined,
      // 'report' means a report-only policy caught it and nothing actually
      // broke; 'enforce' means the resource was refused.
      disposition: event.disposition,
    })
    flushCspViolations()
  })

}

// Init runs synchronously on PHProvider mount (see useEffect below). The
// previous requestIdleCallback wrapper deferred init by up to 2s, which
// caused organic-search bouncers to leave before init ran — their queued
// $pageview never flushed because the buffer lives in memory and the
// transport isn't configured until init completes. DAU dropped ~80%
// overnight (2026-05-03). Note: deferring init does NOT improve LCP,
// because `import posthog from 'posthog-js'` above already pulls the SDK
// bytes into the providers chunk at module-eval time. Only the recorder
// bundle (loaded lazily by armRecorder() after first interaction) is
// worth deferring.
function initPostHog() {
  if (process.env.NODE_ENV !== 'production') return
  if ((posthog as unknown as { __loaded?: boolean }).__loaded) return

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    opt_out_capturing_by_default: false, // US approach: track by default

    // Surveys aren't used on this site — disabling skips the surveys.js
    // download (~31KB) and trims the /decide response payload.
    disable_surveys: true,

    // Autocapture was firing ~14k events/30d (clicks/changes/submits) that
    // weren't powering any dashboard. Every funnel we care about is wired
    // explicitly via `trackEvent` in `lib/analytics.ts`. Off entirely as of
    // 2026-05-15 — the audit decision is documented in
    // [[obsidian/changelog/2026-05]].
    autocapture: false,

    // Start with session replay off so direct landings on /blog never record.
    // armRecorder() opts in after first interaction.
    disable_session_recording: true,
    session_recording: BASE_SESSION_RECORDING_CONFIG,
    // Core Web Vitals only. On/off and replay network timing are left
    // undefined here so the remote project settings still decide them.
    capture_performance: POSTHOG_PERFORMANCE_CONFIG,
    before_send: scrubPostHogEvent,
  })

  armRecorder()

  // Environment super property on every client event, matching the server
  // wrapper in lib/posthog-server-client.ts. `is_internal: true` marks owner/QA
  // browsers (visit any page with ?wgis_internal=1; ?wgis_internal=0 clears
  // it) and the project's test-account filter excludes it. The dev IP is
  // allowlisted in prod, so IP filtering alone cannot separate internal traffic.
  // Super properties persist in PostHog's own storage, so clearing the flag
  // must unregister it too.
  const isInternal = resolveInternalTrafficFlag()
  if (!isInternal) posthog.unregister('is_internal')
  posthog.register({
    // NEXT_PUBLIC_VERCEL_ENV, not a literal: NODE_ENV is 'production' for
    // preview builds too, so a hardcoded label would stamp preview traffic as
    // production (Codex review 2026-08-28, Medium).
    environment: process.env.NEXT_PUBLIC_VERCEL_ENV || 'production',
    ...(isInternal ? { is_internal: true } : {}),
  })

  // Drain anything the CSP listener caught during head parsing / module eval,
  // which is where script-src blocks land.
  flushCspViolations()
}

// Route-driven recorder rules. Pageviews are captured automatically via
// posthog-js `capture_pageview: 'history_change'` — this component only
// suppresses replay on /blog and resumes it elsewhere.
function PostHogRouteEffects() {
  const pathname = usePathname()

  // Stamp the session entry on the FIRST page of the session, before any funnel
  // event runs. recordSessionEntry is idempotent and first-call-wins, but it was
  // only ever invoked lazily by the first funnel event — which fires AFTER the
  // flow has already navigated to `/`. So a visitor who lands on a blog post or
  // the head-term landing page and clicks into the scan recorded entry_surface
  // as `homepage`, which is exactly the attribution the landing-page experiment
  // depends on. This effect has no dependency array entry for `pathname` on
  // purpose: a client-side navigation must NOT rewrite the entry, and the
  // provider does not remount across one, so mount-once is the correct scope.
  useEffect(() => {
    recordSessionEntry()
  }, [])

  // Remember this route's ad eligibility once its markup has committed, for
  // later soft navigations back to it (see adsEligibleByPath).
  useEffect(() => {
    if (!pathname) return
    const timer = setTimeout(recordAdsEligibilityForCurrentPath, 0)
    return () => clearTimeout(timer)
  }, [pathname])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return
    if (!pathname) return

    // Recorder state only changes once the user has activated recording
    // (after first interaction in armRecorder above). Pre-activation, nav
    // between pages is a silent no-op for replay.
    if (!recorderActivated) return

    if (pathname.startsWith('/blog')) {
      posthog.stopSessionRecording()
    } else {
      startSessionRecordingForCurrentRoute()
    }
  }, [pathname])

  return null
}

export function PHProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Before init so the very first pageview's landing context is what gets
    // snapshotted. Idempotent — only a browser's first-ever load writes.
    captureFirstTouchSnapshot()
    initPostHog()
  }, [])

  return (
    <PostHogProvider client={posthog}>
      <PostHogRouteEffects />
      {children}
    </PostHogProvider>
  )
}
